import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Box,
    Alert,
    Card,
    CardContent,
    Grid,
    Chip,
    Skeleton,
    Button,
    IconButton,
    TablePagination,
    Stack
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { appointmentAPI, doctorAPI } from '../../services/api';
import { COLORS } from "../../constants/colors";

// Loading skeleton component
const StatsSkeleton = () => (
    <Paper sx={{ p: 3, mt: 3 }}>
        <Skeleton variant="text" sx={{ fontSize: '2rem', width: '40%' }} />
        <Skeleton variant="text" sx={{ fontSize: '1.5rem', width: '30%', mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 4 }}>
            {[...Array(4)].map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
                </Grid>
            ))}
        </Grid>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
    </Paper>
);

// Chart wrapper component
const ChartWrapper = ({ title, children, height = 400 }) => (
    <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {title}
            </Typography>
            <div style={{ width: '100%', height: height, position: 'relative' }}>
                {children}
            </div>
        </CardContent>
    </Card>
);

// Helper functions
function groupByMonth(appointments) {
    const result = {};
    appointments.forEach(app => {
        const date = new Date(app.scheduledAt);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!result[key]) {
            result[key] = {
                count: 0,
                totalIncome: 0,
                totalProfit: 0
            };
        }
        result[key].count++;
        result[key].totalIncome += app.finalPrice || 0;
        result[key].totalProfit += app.profit || 0;
    });
    return result;
}

function groupByStatus(appointments) {
    const result = {};
    appointments.forEach(app => {
        const status = app.status || 'unknown';
        if (!result[status]) result[status] = 0;
        result[status]++;
    });
    return result;
}

function groupByBranch(appointments) {
    const result = {};
    appointments.forEach(app => {
        const branchName = app.branch?.name || 'Unknown';
        if (!result[branchName]) result[branchName] = 0;
        result[branchName]++;
    });
    return result;
}

function groupByScan(appointments) {
    const result = {};
    appointments.forEach(app => {
        if (app.scans && Array.isArray(app.scans)) {
            app.scans.forEach(scanItem => {
                const scanName = scanItem.scan?.name || 'Unknown';
                if (!result[scanName]) result[scanName] = 0;
                result[scanName] += scanItem.quantity || 1;
            });
        }
    });
    return result;
}

const DoctorStats = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [doctor, setDoctor] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [monthlyStats, setMonthlyStats] = useState({});
    const [statusStats, setStatusStats] = useState({});
    const [branchStats, setBranchStats] = useState({});
    const [scanStats, setScanStats] = useState({});
    const [totals, setTotals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [pagination, setPagination] = useState({
        total: 0,
        pages: 0,
        page: 1,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false
    });

    const fetchData = async (currentPage = page, currentLimit = limit) => {
        try {
            setLoading(true);
            setError(null);

            // Fetch doctor details and appointments with pagination
            const [doctorResponse, appResponse] = await Promise.all([
                doctorAPI.getById(id),
                appointmentAPI.getAllAppointments({
                    doctorId: id,
                    page: currentPage,
                    limit: currentLimit
                })
            ]);

            // Extract nested data correctly
            const doctorData = doctorResponse.data?.data || doctorResponse.data;
            const appointmentsData = appResponse.data?.data || appResponse.data || [];
            const totalsData = appResponse.data?.totals || null;
            const paginationData = appResponse.data?.pagination || null;

            // Ensure appointments is an array
            const appointments = Array.isArray(appointmentsData) ? appointmentsData : [];

            // Group statistics (for charts - using all appointments data)
            const groupedStats = groupByMonth(appointments);
            const statusGrouped = groupByStatus(appointments);
            const branchGrouped = groupByBranch(appointments);
            const scanGrouped = groupByScan(appointments);

            setMonthlyStats(groupedStats);
            setStatusStats(statusGrouped);
            setBranchStats(branchGrouped);
            setScanStats(scanGrouped);
            setTotals(totalsData);
            setDoctor(doctorData);
            setAppointments(appointments);

            // Set pagination data
            if (paginationData) {
                setPagination(paginationData);
            }
        } catch (err) {
            console.error('Error fetching doctor data:', err);
            setError(err.message || t('doctorStats.errorFetching') || 'Error fetching data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchData();
        }
    }, [id, page, limit]);

    // Handle page change
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleLimitChange = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setLimit(newLimit);
        setPage(1); // Reset to first page when changing limit
    };

    // Transform data for charts
    const monthlyChartData = Object.entries(monthlyStats)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, data]) => ({
            month,
            appointments: data.count,
            income: data.totalIncome,
            profit: data.totalProfit
        }));

    const statusChartData = Object.entries(statusStats).map(([status, count]) => ({
        name: t(`appointments.${status}`) || status,
        value: count
    }));

    const branchChartData = Object.entries(branchStats).map(([branch, count]) => ({
        name: branch,
        appointments: count
    }));

    const scanChartData = Object.entries(scanStats)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([scan, count]) => ({
            name: scan,
            count
        }));

    // Show loading skeleton
    if (loading && !appointments.length) {
        return <StatsSkeleton />;
    }

    // Show error state
    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/doctors')}
                    sx={{ mb: 2 }}
                >
                    {t('common.back') || 'Back'}
                </Button>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }} style={{ textAlign: 'right' }}>
            {/* Back Button */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/doctors')}
                sx={{ mb: 2 }}
            >
                {t('common.back') || 'Back to Doctors'}
            </Button>

            {/* Header Card */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="h4" component="h1">
                                {t('doctorStats.title') || 'Doctor Statistics'}
                            </Typography>
                            {doctor && (
                                <Chip
                                    label={doctor.specialization?.name || 'N/A'}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Doctor Info */}
                    {doctor && (
                        <Box>
                            <Typography variant="h5" color="primary" gutterBottom>
                                {t('doctors.dr')} {doctor.name}
                            </Typography>
                            {doctor.contactNumber && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('doctors.phone') || 'Phone'}: {doctor.contactNumber}
                                </Typography>
                            )}
                            {doctor.licenseNumber && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('doctors.license') || 'License'}: {doctor.licenseNumber}
                                </Typography>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'primary.main', color: 'white', height: '100%' }}>
                        <CardContent>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {t('doctorStats.totalReferrals') || 'Total Referrals'}
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1 }}>
                                {pagination.total || totals?.totalAppointments || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'success.main', color: 'white', height: '100%' }}>
                        <CardContent>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {t('doctorStats.totalIncome') || 'Total Income'}
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1 }}>
                                {(totals?.totalIncome || 0).toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'info.main', color: 'white', height: '100%' }}>
                        <CardContent>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {t('doctorStats.totalScans') || 'Total Scans'}
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1 }}>
                                {totals?.totalScans || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'warning.main', color: 'white', height: '100%' }}>
                        <CardContent>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {t('doctorStats.totalPatients') || 'Total Patients'}
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1 }}>
                                {totals?.totalPatients || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts sections remain the same... */}
            {/* Monthly Appointments Bar Chart */}
            <ChartWrapper
                title={t('doctorStats.monthlyReferrals') || 'Monthly Referrals'}
                height={400}
            >
                {monthlyChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthlyChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar
                                dataKey="appointments"
                                fill="#8884d8"
                                name={t('doctorStats.referrals') || 'Referrals'}
                                radius={[4, 4, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Alert severity="info">
                        {t('doctorStats.noDataAvailable') || 'No data available'}
                    </Alert>
                )}
            </ChartWrapper>

            {/* Income & Profit Line Chart */}
            <ChartWrapper
                title={t('doctorStats.monthlyIncomeProfit') || 'Monthly Income & Profit'}
                height={400}
            >
                {monthlyChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={monthlyChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value) => value.toLocaleString()} />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="income"
                                stroke="#82ca9d"
                                strokeWidth={3}
                                dot={{ r: 5 }}
                                name={t('doctorStats.income') || 'Income'}
                            />
                            <Line
                                type="monotone"
                                dataKey="profit"
                                stroke="#8884d8"
                                strokeWidth={3}
                                dot={{ r: 5 }}
                                name={t('doctorStats.profit') || 'Profit'}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Alert severity="info">
                        {t('doctorStats.noDataAvailable') || 'No data available'}
                    </Alert>
                )}
            </ChartWrapper>

            {/* Status & Branch Charts Side by Side */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Status Pie Chart */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                {t('doctorStats.appointmentsByStatus') || 'Appointments by Status'}
                            </Typography>
                            <div style={{ width: '100%', height: 350, position: 'relative' }}>
                                {statusChartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={statusChartData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={true}
                                                label={({ name, percent }) =>
                                                    `${name} (${(percent * 100).toFixed(0)}%)`
                                                }
                                                outerRadius={100}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {statusChartData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={COLORS[index % COLORS.length]}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Alert severity="info">
                                        {t('doctorStats.noDataAvailable') || 'No data available'}
                                    </Alert>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Branch Bar Chart */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                {t('doctorStats.referralsByBranch') || 'Referrals by Branch'}
                            </Typography>
                            <div style={{ width: '100%', height: 350, position: 'relative' }}>
                                {branchChartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={branchChartData} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis type="number" />
                                            <YAxis dataKey="name" type="category" width={100} />
                                            <Tooltip />
                                            <Bar
                                                dataKey="appointments"
                                                fill="#00C49F"
                                                radius={[0, 4, 4, 0]}
                                                name={t('doctorStats.referrals') || 'Referrals'}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Alert severity="info">
                                        {t('doctorStats.noDataAvailable') || 'No data available'}
                                    </Alert>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Top Scans Chart */}
            <ChartWrapper
                title={t('doctorStats.topScansReferred') || 'Top Scans Referred'}
                height={400}
            >
                {scanChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={scanChartData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={150} />
                            <Tooltip />
                            <Legend />
                            <Bar
                                dataKey="count"
                                fill="#FF8042"
                                radius={[0, 4, 4, 0]}
                                name={t('doctorStats.scanCount') || 'Count'}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Alert severity="info">
                        {t('doctorStats.noDataAvailable') || 'No data available'}
                    </Alert>
                )}
            </ChartWrapper>

            {/* Monthly Stats Table */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        {t('doctorStats.monthlyStatsTable') || 'Monthly Statistics Details'}
                    </Typography>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                    {t('doctorStats.month') || 'Month'}
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                    {t('doctorStats.totalReferrals') || 'Total Referrals'}
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                    {t('doctorStats.totalIncome') || 'Total Income'}
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                    {t('doctorStats.totalProfit') || 'Total Profit'}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(monthlyStats).length > 0 ? (
                                Object.entries(monthlyStats)
                                    .sort(([a], [b]) => b.localeCompare(a))
                                    .map(([month, data]) => (
                                        <TableRow key={month} hover>
                                            <TableCell>{month}</TableCell>
                                            <TableCell>{data.count}</TableCell>
                                            <TableCell>{data.totalIncome.toLocaleString()}</TableCell>
                                            <TableCell>{data.totalProfit.toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography color="text.secondary">
                                            {t('doctorStats.noDataAvailable') || 'No data available'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Appointments Details Table WITH PAGINATION */}
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        {t('doctorStats.appointmentsTable') || 'Appointments Details'}
                    </Typography>

                    {/* Pagination Info */}
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('doctorStats.showing') || 'Showing'} {appointments.length} {t('doctorStats.of') || 'of'} {pagination.total} {t('doctorStats.appointments') || 'appointments'}
                        </Typography>
                    </Box>

                    <Box sx={{ overflowX: 'auto' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {t('doctorStats.patient') || 'Patient'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {t('doctorStats.phone') || 'Phone'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {t('doctorStats.scans') || 'Scans'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {t('doctorStats.branch') || 'Branch'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {t('doctorStats.scheduledDate') || 'Scheduled Date'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {t('doctorStats.status') || 'Status'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {t('doctorStats.paymentStatus') || 'Payment Status'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {t('doctorStats.finalPrice') || 'Final Price'}
                                    </TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {t('doctorStats.profit') || 'Profit'}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    // Loading rows
                                    [...Array(limit)].map((_, index) => (
                                        <TableRow key={index}>
                                            <TableCell colSpan={9}>
                                                <Skeleton animation="wave" />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : appointments && appointments.length > 0 ? (
                                    appointments.map((appointment) => (
                                        <TableRow key={appointment._id} hover>
                                            <TableCell>{appointment.patientId?.name || 'N/A'}</TableCell>
                                            <TableCell>{appointment.patientId?.phoneNumber || 'N/A'}</TableCell>
                                            <TableCell>
                                                {appointment.scans && appointment.scans.length > 0 ? (
                                                    <Box>
                                                        {appointment.scans.map((scanItem, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                label={`${scanItem.scan?.name || 'Unknown'} (${scanItem.quantity})`}
                                                                size="small"
                                                                sx={{ m: 0.5 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : 'N/A'}
                                            </TableCell>
                                            <TableCell>{appointment.branch?.name || 'N/A'}</TableCell>
                                            <TableCell>
                                                {new Date(appointment.scheduledAt).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={t(`appointments.${appointment.status}`) || appointment.status}
                                                    color={
                                                        appointment.status === 'completed' ? 'success' :
                                                            appointment.status === 'in_progress' ? 'info' :
                                                                appointment.status === 'scheduled' ? 'default' :
                                                                    'error'
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={appointment.isPaid ? t('doctorStats.paid') || 'Paid' : t('doctorStats.unpaid') || 'Unpaid'}
                                                    color={appointment.isPaid ? 'success' : 'warning'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{appointment.finalPrice?.toLocaleString() || 0}</TableCell>
                                            <TableCell>{appointment.profit?.toLocaleString() || 0}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            <Typography color="text.secondary">
                                                {t('doctorStats.noDataAvailable') || 'No data available'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>

                    {/* Custom Pagination Controls */}
                    <Box sx={{
                        mt: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        {/* Rows per page selector */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                                {t('doctorStats.rowsPerPage') || 'Rows per page:'}
                            </Typography>
                            <select
                                value={limit}
                                onChange={handleLimitChange}
                                style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '14px'
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </Box>

                        {/* Page info and navigation */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2">
                                {t('doctorStats.page') || 'Page'} {pagination.page} {t('doctorStats.of') || 'of'} {pagination.pages}
                            </Typography>

                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={!pagination.hasPrevPage || loading}
                                    startIcon={<NavigateBeforeIcon />}
                                >
                                    {t('common.previous') || 'Previous'}
                                </Button>

                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={!pagination.hasNextPage || loading}
                                    endIcon={<NavigateNextIcon />}
                                >
                                    {t('common.next') || 'Next'}
                                </Button>
                            </Stack>
                        </Box>
                    </Box>


                    <TablePagination
                        component="div"
                        count={pagination.total}
                        page={page - 1} // MUI uses 0-based indexing
                        onPageChange={(event, newPage) => handlePageChange(newPage + 1)}
                        rowsPerPage={limit}
                        onRowsPerPageChange={handleLimitChange}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        labelRowsPerPage={t('doctorStats.rowsPerPage') || 'Rows per page:'}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} ${t('doctorStats.of') || 'of'} ${count !== -1 ? count : `more than ${to}`}`
                        }
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default DoctorStats;